import type { SeedScenario } from './seedLatinScenarios'

/**
 * A second wave of scenarios, built on the expansion islands.
 *
 * The first three per language are social — a party, a restaurant, a train.
 * These three are the situations where getting it wrong actually costs you
 * something: missing a train, buying the wrong thing, being sent away with a
 * problem unsolved. That is deliberate. A scenario is only worth drilling if
 * failing it in real life would sting.
 */
export const MORE_SCENARIOS: Record<string, SeedScenario[]> = {
  de: [
    {
      slug: 'bahnhof',
      title: 'Making the connection',
      nativeTitle: 'Am Hauptbahnhof',
      setting: 'Hauptbahnhof, elf Minuten bis zur Abfahrt, keine Anzeige',
      brief:
        'Eleven minutes until your train and the departure board is showing nothing useful. The man at the counter is helpful but fast, and every question you fumble costs you a minute you do not have.',
      beats: [
        {
          speaker: 'Schalterbeamter',
          prompt: 'Guten Tag, was kann ich für Sie tun?',
          english: 'Good afternoon, what can I do for you?',
          cue: 'Buy a return to Munich.',
          island: 'de_unterwegs',
          blocks: ['de_unterwegs_b3'],
          hint: 'Einmal nach München — nobody says eine Fahrkarte at the counter.',
        },
        {
          speaker: 'Schalterbeamter',
          prompt: 'Hin und zurück, alles klar. Sonst noch etwas?',
          english: 'Return, right. Anything else?',
          cue: 'You still do not know where the train leaves from.',
          island: 'de_unterwegs',
          blocks: ['de_unterwegs_b4'],
        },
        {
          speaker: 'Schalterbeamter',
          prompt: 'Gleis neun. Aber beeilen Sie sich, der fährt gleich.',
          english: 'Platform nine. But hurry, it leaves shortly.',
          cue: 'You have no idea how to get to platform nine.',
          island: 'de_unterwegs',
          blocks: ['de_unterwegs_b1'],
        },
        {
          speaker: 'Mitreisende',
          prompt: 'Sie sehen verloren aus — wo wollen Sie denn hin?',
          english: 'You look lost — where are you trying to get to?',
          cue: 'Ask her to tell you when to get off.',
          island: 'de_unterwegs',
          blocks: ['de_unterwegs_b5'],
          hint: 'The indirect question sends the verb to the end: wann ich aussteigen muss.',
        },
      ],
    },
    {
      slug: 'laden',
      title: 'Buying something that fits',
      nativeTitle: 'Im Laden',
      setting: 'Ein Kaufhaus, Samstagnachmittag, die Verkäuferin kommt sofort',
      brief:
        'A department store on a Saturday. The assistant reaches you within seconds, which means the "just looking" line has to be ready before you have decided anything.',
      beats: [
        {
          speaker: 'Verkäuferin',
          prompt: 'Guten Tag! Kann ich Ihnen helfen?',
          english: 'Hello! Can I help you?',
          cue: 'Buy yourself a minute.',
          island: 'de_einkaufen',
          blocks: ['de_einkaufen_b2'],
        },
        {
          speaker: 'Verkäuferin',
          prompt: 'Natürlich, schauen Sie sich in Ruhe um.',
          english: 'Of course, take your time looking.',
          cue: 'This one is too small.',
          island: 'de_einkaufen',
          blocks: ['de_einkaufen_b1'],
        },
        {
          speaker: 'Verkäuferin',
          prompt: 'Da habe ich noch eine Nummer größer. Möchten Sie sie anprobieren?',
          english: 'I have one size larger. Would you like to try it?',
          cue: 'Find out what it costs first.',
          island: 'de_einkaufen',
          blocks: ['de_einkaufen_b3'],
        },
        {
          speaker: 'Verkäuferin',
          prompt: 'Neunundvierzig Euro. Zahlen Sie bar oder mit Karte?',
          english: 'Forty-nine euros. Are you paying cash or by card?',
          cue: 'You have no cash.',
          island: 'de_einkaufen',
          blocks: ['de_einkaufen_b4'],
          hint: 'Worth asking anywhere in Germany — plenty of places are still cash only.',
        },
      ],
    },
    {
      slug: 'reklamation',
      title: 'Something went wrong',
      nativeTitle: 'Eine Reklamation',
      setting: 'Kundendienst, Montagmorgen, die Schlange hinter Ihnen wächst',
      brief:
        'Customer service on a Monday morning with a queue behind you. Complaining in a second language is where politeness and clarity pull against each other — these blocks do both at once.',
      beats: [
        {
          speaker: 'Mitarbeiter',
          prompt: 'Der Nächste, bitte. Was gibt es?',
          english: 'Next, please. What is it?',
          cue: 'Open without accusing anyone.',
          island: 'de_probleme',
          blocks: ['de_probleme_b1'],
        },
        {
          speaker: 'Mitarbeiter',
          prompt: 'Und was genau ist das Problem?',
          english: 'And what exactly is the problem?',
          cue: 'State the facts plainly.',
          island: 'de_probleme',
          blocks: ['de_probleme_b2'],
        },
        {
          speaker: 'Mitarbeiter',
          prompt: 'Da müsste ich erst mal im System nachsehen, ob die Sendung überhaupt rausgegangen ist.',
          english: 'I would have to check the system first to see whether it even went out.',
          cue: 'You lost that. Ask him to slow down.',
          island: 'de_probleme',
          blocks: ['de_probleme_b5'],
        },
        {
          speaker: 'Mitarbeiter',
          prompt: 'Entschuldigung. Ich schaue nach, ob das Paket verschickt wurde.',
          english: 'Sorry. I am checking whether the parcel was sent.',
          cue: 'Ask for his help directly.',
          island: 'de_probleme',
          blocks: ['de_probleme_b3'],
        },
      ],
    },
  ],

  fr: [
    {
      slug: 'gare',
      title: 'Making the connection',
      nativeTitle: 'À la gare',
      setting: 'Gare de Lyon, onze minutes avant le départ, aucun affichage utile',
      brief:
        'Eleven minutes before your train and the board is showing nothing useful. The man at the window is helpful but quick, and every fumbled question costs a minute you do not have.',
      beats: [
        {
          speaker: 'Guichetier',
          prompt: 'Bonjour, je vous écoute.',
          english: "Hello, I'm listening.",
          cue: 'Buy a return to Lyon.',
          island: 'fr_deplacements',
          blocks: ['fr_deplacements_b3'],
        },
        {
          speaker: 'Guichetier',
          prompt: 'Aller-retour, très bien. Autre chose ?',
          english: 'Return, very good. Anything else?',
          cue: 'You still do not know the platform.',
          island: 'fr_deplacements',
          blocks: ['fr_deplacements_b4'],
        },
        {
          speaker: 'Guichetier',
          prompt: 'Quai neuf. Dépêchez-vous, il part dans dix minutes.',
          english: 'Platform nine. Hurry, it leaves in ten minutes.',
          cue: 'You do not know the way.',
          island: 'fr_deplacements',
          blocks: ['fr_deplacements_b1'],
        },
        {
          speaker: 'Voyageuse',
          prompt: 'Vous avez l’air perdu — vous allez où ?',
          english: 'You look lost — where are you going?',
          cue: 'Ask her to tell you when to get off.',
          island: 'fr_deplacements',
          blocks: ['fr_deplacements_b5'],
        },
      ],
    },
    {
      slug: 'magasin',
      title: 'Buying something that fits',
      nativeTitle: 'Dans le magasin',
      setting: 'Un grand magasin, samedi après-midi, la vendeuse arrive tout de suite',
      brief:
        'A department store on a Saturday. The assistant reaches you within seconds, so the "just looking" line has to be ready before you have decided anything.',
      beats: [
        {
          speaker: 'Vendeuse',
          prompt: 'Bonjour ! Je peux vous aider ?',
          english: 'Hello! Can I help you?',
          cue: 'Buy yourself a minute.',
          island: 'fr_courses',
          blocks: ['fr_courses_b2'],
        },
        {
          speaker: 'Vendeuse',
          prompt: 'Je vous en prie, prenez votre temps.',
          english: 'Please do, take your time.',
          cue: 'This one is too small.',
          island: 'fr_courses',
          blocks: ['fr_courses_b1'],
        },
        {
          speaker: 'Vendeuse',
          prompt: 'J’ai la taille au-dessus. Vous voulez l’essayer ?',
          english: 'I have the next size up. Would you like to try it?',
          cue: 'Find out what it costs first.',
          island: 'fr_courses',
          blocks: ['fr_courses_b3'],
        },
        {
          speaker: 'Vendeuse',
          prompt: 'Quarante-neuf euros. Vous réglez comment ?',
          english: 'Forty-nine euros. How are you paying?',
          cue: 'You have no cash.',
          island: 'fr_courses',
          blocks: ['fr_courses_b4'],
        },
      ],
    },
    {
      slug: 'reclamation',
      title: 'Something went wrong',
      nativeTitle: 'Une réclamation',
      setting: 'Service client, lundi matin, la queue s’allonge derrière vous',
      brief:
        'Customer service on a Monday with a queue behind you. Complaining in a second language is where politeness and clarity pull against each other — these blocks do both.',
      beats: [
        {
          speaker: 'Employé',
          prompt: 'Suivant, s’il vous plaît. Je vous écoute.',
          english: "Next, please. I'm listening.",
          cue: 'Open without accusing anyone.',
          island: 'fr_problemes',
          blocks: ['fr_problemes_b1'],
        },
        {
          speaker: 'Employé',
          prompt: 'C’est-à-dire ? Quel est le problème exactement ?',
          english: 'Meaning? What exactly is the problem?',
          cue: 'State the facts plainly.',
          island: 'fr_problemes',
          blocks: ['fr_problemes_b2'],
        },
        {
          speaker: 'Employé',
          prompt: 'Il faudrait que je vérifie dans le système si le colis est bien parti.',
          english: 'I would need to check in the system whether the parcel actually went out.',
          cue: 'You lost that. Ask him to slow down.',
          island: 'fr_problemes',
          blocks: ['fr_problemes_b5'],
        },
        {
          speaker: 'Employé',
          prompt: 'Pardon. Je regarde si le colis a été expédié.',
          english: 'Sorry. I am checking whether the parcel was shipped.',
          cue: 'Ask for his help directly.',
          island: 'fr_problemes',
          blocks: ['fr_problemes_b3'],
        },
      ],
    },
  ],

  es: [
    {
      slug: 'estacion',
      title: 'Making the connection',
      nativeTitle: 'En la estación',
      setting: 'Atocha, once minutos para la salida, los paneles no ayudan',
      brief:
        'Eleven minutes before your train and the boards are no help. The man at the window is helpful but quick, and every fumbled question costs a minute you do not have.',
      beats: [
        {
          speaker: 'Taquillero',
          prompt: 'Buenos días, ¿qué necesita?',
          english: 'Good morning, what do you need?',
          cue: 'Buy a return to Seville.',
          island: 'es_moverse',
          blocks: ['es_moverse_b3'],
        },
        {
          speaker: 'Taquillero',
          prompt: 'Ida y vuelta, muy bien. ¿Algo más?',
          english: 'Return, very good. Anything else?',
          cue: 'You still do not know the platform.',
          island: 'es_moverse',
          blocks: ['es_moverse_b4'],
        },
        {
          speaker: 'Taquillero',
          prompt: 'Andén nueve. Dese prisa, que sale enseguida.',
          english: 'Platform nine. Hurry up, it leaves right away.',
          cue: 'You do not know the way.',
          island: 'es_moverse',
          blocks: ['es_moverse_b1'],
        },
        {
          speaker: 'Viajera',
          prompt: 'Parece perdido — ¿adónde va?',
          english: 'You look lost — where are you going?',
          cue: 'Ask her to tell you when to get off.',
          island: 'es_moverse',
          blocks: ['es_moverse_b5'],
          hint: 'cuando + subjunctive: cuando tenga que bajarme.',
        },
      ],
    },
    {
      slug: 'tienda',
      title: 'Buying something that fits',
      nativeTitle: 'En la tienda',
      setting: 'Unos grandes almacenes, sábado por la tarde, la dependienta llega enseguida',
      brief:
        'A department store on a Saturday. The assistant reaches you within seconds, so the "just looking" line has to be ready before you have decided anything.',
      beats: [
        {
          speaker: 'Dependienta',
          prompt: '¡Buenas! ¿Le puedo ayudar?',
          english: 'Hello! Can I help you?',
          cue: 'Buy yourself a minute.',
          island: 'es_compras',
          blocks: ['es_compras_b2'],
        },
        {
          speaker: 'Dependienta',
          prompt: 'Claro, mire con calma.',
          english: 'Of course, look at your leisure.',
          cue: 'This one is too small.',
          island: 'es_compras',
          blocks: ['es_compras_b1'],
        },
        {
          speaker: 'Dependienta',
          prompt: 'Tengo una talla más. ¿Se la quiere probar?',
          english: 'I have one size up. Would you like to try it?',
          cue: 'Find out what it costs first.',
          island: 'es_compras',
          blocks: ['es_compras_b3'],
        },
        {
          speaker: 'Dependienta',
          prompt: 'Cuarenta y nueve euros. ¿Cómo va a pagar?',
          english: 'Forty-nine euros. How are you going to pay?',
          cue: 'You have no cash.',
          island: 'es_compras',
          blocks: ['es_compras_b4'],
        },
      ],
    },
    {
      slug: 'reclamacion',
      title: 'Something went wrong',
      nativeTitle: 'Una reclamación',
      setting: 'Atención al cliente, lunes por la mañana, la cola crece detrás',
      brief:
        'Customer service on a Monday with a queue behind you. Complaining in a second language is where politeness and clarity pull against each other — these blocks do both.',
      beats: [
        {
          speaker: 'Empleado',
          prompt: 'Siguiente, por favor. Usted dirá.',
          english: 'Next, please. Go ahead.',
          cue: 'Open without accusing anyone.',
          island: 'es_problemas',
          blocks: ['es_problemas_b1'],
        },
        {
          speaker: 'Empleado',
          prompt: '¿Y cuál es el problema exactamente?',
          english: 'And what exactly is the problem?',
          cue: 'State the facts plainly.',
          island: 'es_problemas',
          blocks: ['es_problemas_b2'],
        },
        {
          speaker: 'Empleado',
          prompt: 'Tendría que mirar en el sistema si el paquete llegó a salir siquiera.',
          english: 'I would have to check the system to see whether the parcel even went out.',
          cue: 'You lost that. Ask him to slow down.',
          island: 'es_problemas',
          blocks: ['es_problemas_b5'],
        },
        {
          speaker: 'Empleado',
          prompt: 'Perdone. Estoy mirando si se envió el paquete.',
          english: 'Sorry. I am checking whether the parcel was sent.',
          cue: 'Ask for his help directly.',
          island: 'es_problemas',
          blocks: ['es_problemas_b3'],
        },
      ],
    },
  ],

  it: [
    {
      slug: 'stazione',
      title: 'Making the connection',
      nativeTitle: 'In stazione',
      setting: 'Stazione centrale, undici minuti alla partenza, i tabelloni non aiutano',
      brief:
        'Eleven minutes before your train and the boards are no help. The man at the window is helpful but quick, and every fumbled question costs a minute you do not have.',
      beats: [
        {
          speaker: 'Bigliettaio',
          prompt: 'Buongiorno, mi dica.',
          english: 'Good morning, go ahead.',
          cue: 'Buy a return to Florence.',
          island: 'it_spostarsi',
          blocks: ['it_spostarsi_b3'],
        },
        {
          speaker: 'Bigliettaio',
          prompt: 'Andata e ritorno, benissimo. Altro?',
          english: 'Return, very good. Anything else?',
          cue: 'You still do not know the platform.',
          island: 'it_spostarsi',
          blocks: ['it_spostarsi_b4'],
        },
        {
          speaker: 'Bigliettaio',
          prompt: 'Binario nove. Si sbrighi, parte tra poco.',
          english: 'Platform nine. Hurry, it leaves shortly.',
          cue: 'You do not know the way.',
          island: 'it_spostarsi',
          blocks: ['it_spostarsi_b1'],
        },
        {
          speaker: 'Viaggiatrice',
          prompt: 'Sembra perso — dove deve andare?',
          english: 'You look lost — where do you need to go?',
          cue: 'Ask her to tell you when to get off.',
          island: 'it_spostarsi',
          blocks: ['it_spostarsi_b5'],
        },
      ],
    },
    {
      slug: 'negozio',
      title: 'Buying something that fits',
      nativeTitle: 'In negozio',
      setting: 'Un grande magazzino, sabato pomeriggio, la commessa arriva subito',
      brief:
        'A department store on a Saturday. The assistant reaches you within seconds, so the "just looking" line has to be ready before you have decided anything.',
      beats: [
        {
          speaker: 'Commessa',
          prompt: 'Buongiorno! Posso aiutarla?',
          english: 'Good morning! Can I help you?',
          cue: 'Buy yourself a minute.',
          island: 'it_spesa',
          blocks: ['it_spesa_b2'],
        },
        {
          speaker: 'Commessa',
          prompt: 'Prego, faccia con calma.',
          english: 'Please, take your time.',
          cue: 'This one is too small.',
          island: 'it_spesa',
          blocks: ['it_spesa_b1'],
        },
        {
          speaker: 'Commessa',
          prompt: 'Ho una taglia in più. Vuole provarla?',
          english: 'I have one size up. Would you like to try it?',
          cue: 'Find out what it costs first.',
          island: 'it_spesa',
          blocks: ['it_spesa_b3'],
        },
        {
          speaker: 'Commessa',
          prompt: 'Quarantanove euro. Come preferisce pagare?',
          english: 'Forty-nine euros. How would you prefer to pay?',
          cue: 'You have no cash.',
          island: 'it_spesa',
          blocks: ['it_spesa_b4'],
        },
      ],
    },
    {
      slug: 'reclamo',
      title: 'Something went wrong',
      nativeTitle: 'Un reclamo',
      setting: 'Servizio clienti, lunedì mattina, la fila dietro di lei si allunga',
      brief:
        'Customer service on a Monday with a queue behind you. Complaining in a second language is where politeness and clarity pull against each other — these blocks do both.',
      beats: [
        {
          speaker: 'Impiegato',
          prompt: 'Il prossimo, prego. Mi dica.',
          english: 'Next, please. Go ahead.',
          cue: 'Open without accusing anyone.',
          island: 'it_problemi',
          blocks: ['it_problemi_b1'],
        },
        {
          speaker: 'Impiegato',
          prompt: 'E qual è il problema esattamente?',
          english: 'And what exactly is the problem?',
          cue: 'State the facts plainly.',
          island: 'it_problemi',
          blocks: ['it_problemi_b2'],
        },
        {
          speaker: 'Impiegato',
          prompt: 'Dovrei controllare a sistema se il pacco è mai partito.',
          english: 'I would have to check the system to see whether the parcel ever went out.',
          cue: 'You lost that. Ask him to slow down.',
          island: 'it_problemi',
          blocks: ['it_problemi_b5'],
        },
        {
          speaker: 'Impiegato',
          prompt: 'Scusi. Sto controllando se il pacco è stato spedito.',
          english: 'Sorry. I am checking whether the parcel was sent.',
          cue: 'Ask for his help directly.',
          island: 'it_problemi',
          blocks: ['it_problemi_b3'],
        },
      ],
    },
  ],
}
